import { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { BedDouble, Check, Sparkles, Loader2, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const fmtDate = (v) => (v ? new Date(v.slice(0, 10) + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const nights = (a, d) => Math.max(1, Math.round((new Date((d || "").slice(0, 10)) - new Date((a || "").slice(0, 10))) / 86400000) || 1);

/**
 * ASSIGN-ROOM · pick or change the room of a not-yet-checked-in reservation.
 * Available rooms are filtered by the reserved room type + the guest's stay dates (occupied / overlapping / blocked rooms excluded);
 * "Show other room types" widens the pool for an upgrade or switch. `onAssigned(updatedReservation)` fires after the save.
 */
export function AssignRoomDialog({ reservation: r, open, onOpenChange, onAssigned, onPick, excludeRoomIds = [] }) {
  const [rooms, setRooms] = useState([]);
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [picked, setPicked] = useState("");
  const [busy, setBusy] = useState(false);
  const [widened, setWidened] = useState(false);
  const typeId = r?.requested_room_type_id || "";

  const load = useCallback(async () => {
    if (!open || !r?.id) return;
    setLoading(true);
    try {
      const p = new URLSearchParams({ limit: "200", include_dirty: "true", exclude_reservation_id: r.id });
      if (r.hotel_id) p.set("hotel_id", r.hotel_id);
      if (r.arrival_date) p.set("arrival_date", r.arrival_date.slice(0, 10));
      if (r.departure_date) p.set("departure_date", r.departure_date.slice(0, 10));
      if (typeId && !showAll) p.set("room_type_id", typeId);
      const { data } = await api.get(`/inventory/available?${p.toString()}`);
      setRooms((data.items || []).filter((x) => !excludeRoomIds.includes(x.room_id || x.id)));
      setDiag(data.diagnostics || null);
      // nothing in the requested type → widen automatically so the desk isn't left with an empty panel
      if ((data.items || []).length === 0 && typeId && !showAll) { setShowAll(true); setWidened(true); }
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  }, [open, r?.id, r?.hotel_id, r?.arrival_date, r?.departure_date, typeId, showAll, excludeRoomIds.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (open) { setPicked(r?.assigned_room_id || ""); setShowAll(false); setWidened(false); } }, [open, r?.assigned_room_id]);
  useEffect(() => { load(); }, [load]);

  const groups = useMemo(() => {
    const byType = new Map();
    rooms.forEach((x) => {
      const k = x.room_type_name || x.room_type_code || "Other";
      if (!byType.has(k)) byType.set(k, { name: k, match: !!typeId && x.room_type_id === typeId, rooms: [] });
      byType.get(k).rooms.push(x);
    });
    return [...byType.values()].sort((a, b) => (a.match === b.match ? a.name.localeCompare(b.name) : a.match ? -1 : 1));
  }, [rooms, typeId]);

  const confirm = async () => {
    if (!picked) { toast.error("Select a room first"); return; }
    setBusy(true);
    if (onPick) {  // caller owns the save (e.g. one line of a group booking)
      try { await onPick(picked, rooms.find((x) => (x.room_id || x.id) === picked)); onOpenChange(false); }
      catch (e) { const body = e?.response?.data || {}; const d = body.code ? body : body.detail || {}; toast.error(d.reason || d.message || formatApiError(e)); }
      setBusy(false); return;
    }
    try {
      const { data } = await api.post(`/pms/reservations/${r.id}/assign-room`, { room_id: picked });
      toast.success(`Room ${data.room_number} assigned to ${r.code}${data.upgrade ? " · room type differs from the request" : ""}`);
      onOpenChange(false);
      onAssigned?.(data);
    } catch (e) {
      const body = e?.response?.data || {};
      const d = body.code ? body : body.detail || {};
      toast.error(d.reason || d.message || formatApiError(e));
    }
    setBusy(false);
  };

  if (!r) return null;
  const current = r.room_number;
  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="bg-hocp-bgCard border border-hocp-border md:max-w-[680px] max-h-[88vh] overflow-y-auto" data-hocp-dialog data-testid="assign-room-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><BedDouble size={18} strokeWidth={1.5} />{current ? `Change room · ${r.code}` : `Assign room · ${r.code}`}</DialogTitle>
          <DialogDescription>
            {r.guest_name || "Guest"} · {fmtDate(r.arrival_date)} → {fmtDate(r.departure_date)} · {nights(r.arrival_date, r.departure_date)} night{nights(r.arrival_date, r.departure_date) === 1 ? "" : "s"}
            {r.room_type_name ? ` · ${r.room_type_name}` : ""}{current ? ` · currently room ${current}` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between gap-2 text-[12px] text-hocp-textMuted">
          <span data-testid="assign-room-count">{loading ? "Finding available rooms…" : `${rooms.length} room${rooms.length === 1 ? "" : "s"} available for these dates`}</span>
          {typeId && (
            <button type="button" onClick={() => setShowAll((v) => !v)} className="inline-flex items-center gap-1 text-hocp-deepTeal hover:underline" data-testid="assign-room-show-all">
              <ArrowUpRight size={13} />{showAll ? "Only the reserved room type" : "Show other room types"}
            </button>
          )}
        </div>

        {widened && showAll && !loading && (
          <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-[12px] text-sky-900" data-testid="assign-room-widened">
            No {r.room_type_name || "reserved-type"} rooms are free for these dates — showing every other room type (the guest will be upgraded or switched).
          </div>
        )}
        {loading && <div className="py-8 flex items-center justify-center gap-2 text-[13px] text-hocp-textMuted"><Loader2 size={16} className="animate-spin" />Loading…</div>}
        {!loading && rooms.length === 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[12.5px] text-amber-900" data-testid="assign-room-empty">
            No rooms are free for these dates{typeId && !showAll ? " in the reserved room type" : ""}.
            {diag?.excluded && <span className="block mt-1 text-amber-800">{[diag.excluded.occupied && `${diag.excluded.occupied} occupied`, diag.excluded.reserved_overlap && `${diag.excluded.reserved_overlap} reserved`, diag.excluded.oos_ooo_blocked && `${diag.excluded.oos_ooo_blocked} out of order / blocked`].filter(Boolean).join(" · ")}</span>}
          </div>
        )}
        {!loading && groups.map((g) => (
          <div key={g.name} className="space-y-1.5" data-testid={`assign-room-group-${g.name}`}>
            <div className="text-[11px] mono uppercase tracking-[0.12em] text-hocp-textMuted flex items-center gap-1.5">
              {g.name}{g.match && <span className="text-hocp-deepTeal normal-case tracking-normal">· reserved type</span>}
              {!g.match && typeId && <span className="inline-flex items-center gap-0.5 text-sky-700 normal-case tracking-normal"><Sparkles size={11} />upgrade / switch</span>}
            </div>
            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-1.5">
              {g.rooms.map((x) => {
                const id = x.room_id || x.id, sel = picked === id, dirty = x.housekeeping_status === "dirty";
                return (
                  <button key={id} type="button" onClick={() => setPicked(id)} data-testid={`assign-room-option-${x.room_number}`} data-selected={sel ? "1" : "0"}
                    className={`relative rounded-md border px-2 py-2 text-left transition-colors ${sel ? "border-hocp-deepTeal bg-hocp-softIce ring-1 ring-hocp-deepTeal" : "border-hocp-border hover:border-hocp-deepTeal/60 hover:bg-hocp-bgSubtle"}`}>
                    <div className="mono font-semibold text-[14px] text-hocp-textPrimary">{x.room_number}</div>
                    <div className={`text-[10px] ${x.vacating_on ? "text-sky-700" : dirty ? "text-amber-600" : "text-emerald-600"}`}>{x.vacating_on ? `in-house · leaves ${fmtDate(x.vacating_on).slice(0, 6)}` : dirty ? "dirty · HK" : "clean"}</div>
                    {sel && <Check size={13} className="absolute top-1.5 right-1.5 text-hocp-deepTeal" />}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="assign-room-cancel">Cancel</Button>
          <Button onClick={confirm} disabled={busy || !picked || picked === r.assigned_room_id} data-testid="assign-room-confirm">
            {busy ? "Saving…" : picked ? `${current ? "Change to" : "Assign"} room ${rooms.find((x) => (x.room_id || x.id) === picked)?.room_number || ""}` : "Select a room"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
